import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Plus, Trash2, FolderOpen, Globe, FileUp, ClipboardType, Loader2, RefreshCw, Search,
  FileText, FileSpreadsheet, Presentation, Mail, Mic, StickyNote, Folder,
} from "lucide-react";

const SOURCE_ICONS = { file: FileText, url: Globe, note: StickyNote, transcript: Mic, email: Mail };

function DocIcon({ doc }) {
  const ext = (doc.original_filename || "").split(".").pop()?.toLowerCase();
  let Icon = SOURCE_ICONS[doc.source_type] || FileText;
  if (ext === "xlsx" || ext === "xls") Icon = FileSpreadsheet;
  if (ext === "pptx" || ext === "ppt") Icon = Presentation;
  return <Icon size={15} className="text-slate-400 shrink-0" />;
}

export default function SbLibrary() {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWs, setActiveWs] = useState(null);
  const [folder, setFolder] = useState(null);
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");
  const [showWsDialog, setShowWsDialog] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [detail, setDetail] = useState(null);
  const pollRef = useRef(null);

  const ws = workspaces.find((w) => w.id === activeWs);

  const loadWorkspaces = useCallback(async () => {
    const r = await api.get("/second-brain/workspaces");
    setWorkspaces(r.data);
    if (!activeWs && r.data.length) setActiveWs(r.data[0].id);
  }, [activeWs]);

  const loadDocs = useCallback(async () => {
    if (!activeWs) { setDocs([]); return; }
    const params = { workspace_id: activeWs };
    if (folder) params.folder = folder;
    if (search) params.q = search;
    const r = await api.get("/second-brain/documents", { params });
    setDocs(r.data);
  }, [activeWs, folder, search]);

  useEffect(() => { loadWorkspaces(); }, [loadWorkspaces]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  useEffect(() => {
    const anyProcessing = docs.some((d) => d.status === "processing");
    clearInterval(pollRef.current);
    if (anyProcessing) pollRef.current = setInterval(loadDocs, 4000);
    return () => clearInterval(pollRef.current);
  }, [docs, loadDocs]);

  const deleteDoc = async (id) => {
    await api.delete(`/second-brain/documents/${id}`);
    toast.success("Document removed from knowledge base");
    loadDocs();
  };

  const addFolder = async () => {
    const name = window.prompt("New folder name:");
    if (!name?.trim()) return;
    await api.post(`/second-brain/workspaces/${activeWs}/folders`, { name });
    loadWorkspaces();
  };

  return (
    <div className="flex h-full" data-testid="sb-library">
      <aside className="w-56 shrink-0 border-r border-slate-200 bg-[#fafbfc] flex-col hidden md:flex">
        <div className="px-3 pt-4 pb-2 flex items-center justify-between">
          <span className="gc-label">Workspaces</span>
          <button onClick={() => setShowWsDialog(true)} className="text-slate-400 hover:text-[#0a2540]" data-testid="sb-new-workspace-btn">
            <Plus size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 space-y-0.5">
          {workspaces.map((w) => (
            <button
              key={w.id}
              onClick={() => { setActiveWs(w.id); setFolder(null); }}
              data-testid={`sb-workspace-${w.id}`}
              className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-xs text-left ${
                w.id === activeWs ? "bg-slate-100 text-[#0a2540] font-medium" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <FolderOpen size={13} className="shrink-0" />
              <span className="flex-1 truncate">{w.name}</span>
              <span className="text-[10px] text-slate-400">{w.document_count}</span>
            </button>
          ))}
          {workspaces.length === 0 && (
            <div className="px-3 py-3 text-[11px] text-slate-400 italic">Create a workspace to start uploading knowledge.</div>
          )}
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="px-4 sm:px-6 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
          <div className="md:hidden">
            <Select value={activeWs || ""} onValueChange={(v) => { setActiveWs(v); setFolder(null); }}>
              <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Workspace" /></SelectTrigger>
              <SelectContent>
                {workspaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents…" className="h-8 pl-8 w-48 sm:w-64 text-xs" data-testid="sb-doc-search" />
          </div>
          <div className="flex-1" />
          <Button onClick={() => setShowAdd(true)} disabled={!activeWs} className="h-8 bg-[#0a2540] hover:bg-[#0a2540]/90 text-xs" data-testid="sb-add-knowledge-btn">
            <Plus size={13} className="mr-1" /> Add knowledge
          </Button>
        </div>

        {ws && (
          <div className="px-4 sm:px-6 py-2.5 flex flex-wrap gap-1.5 border-b border-slate-100">
            <FolderChip label="All" active={!folder} onClick={() => setFolder(null)} />
            {(ws.folders || []).map((f) => (
              <FolderChip key={f} label={f} active={folder === f} onClick={() => setFolder(f)} />
            ))}
            <button onClick={addFolder} className="text-[11px] text-slate-400 hover:text-[#0a2540] px-1.5" data-testid="sb-add-folder-btn">+ folder</button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {docs.length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-400">
              {activeWs ? "No documents here yet. Click “Add knowledge” to upload." : "Create a workspace first."}
            </div>
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
              {docs.map((d) => (
                <div
                  key={d.id}
                  onClick={() => setDetail(d)}
                  data-testid={`sb-doc-card-${d.id}`}
                  className="group border border-slate-200 rounded-md p-3.5 bg-white hover:border-[#0a2540]/40 hover:shadow-sm cursor-pointer transition-all"
                >
                  <div className="flex items-start gap-2">
                    <DocIcon doc={d} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-slate-800 truncate">{d.title}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {d.folder && <span className="inline-flex items-center gap-0.5 mr-2"><Folder size={9} />{d.folder}</span>}
                        {(d.created_at || "").slice(0, 10)}
                      </div>
                    </div>
                    {d.status === "processing" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-blue-600" data-testid="sb-doc-processing">
                        <Loader2 size={11} className="animate-spin" /> AI
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteDoc(d.id); }}
                        className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500"
                        data-testid={`sb-doc-delete-${d.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                  {d.summary && <p className="text-[11px] text-slate-500 mt-2 line-clamp-2">{d.summary}</p>}
                  {(d.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {d.tags.slice(0, 4).map((t) => (
                        <Badge key={t} variant="secondary" className="text-[9px] px-1.5 py-0 font-normal">{t}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <NewWorkspaceDialog open={showWsDialog} onClose={() => setShowWsDialog(false)} onCreated={(w) => { loadWorkspaces(); setActiveWs(w.id); }} />
      {activeWs && <AddKnowledgeDialog open={showAdd} onClose={() => setShowAdd(false)} workspaceId={activeWs} folders={ws?.folders || []} onAdded={loadDocs} />}
      <DocDetailDialog doc={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function FolderChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`sb-folder-chip-${label.toLowerCase()}`}
      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
        active ? "bg-[#0a2540] text-white border-[#0a2540]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
      }`}
    >
      {label}
    </button>
  );
}

function NewWorkspaceDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/second-brain/workspaces", { name, description });
      toast.success("Workspace created");
      onCreated(r.data);
      setName(""); setDescription("");
      onClose();
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>New workspace</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Client — Acme Corp, Finance Dept" data-testid="sb-ws-name-input" />
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" rows={2} data-testid="sb-ws-desc-input" />
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={busy || !name.trim()} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="sb-ws-create-btn">
            {busy && <Loader2 size={13} className="mr-1.5 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddKnowledgeDialog({ open, onClose, workspaceId, folders, onAdded }) {
  const [mode, setMode] = useState("file");
  const [folder, setFolder] = useState("__auto__");
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [sourceType, setSourceType] = useState("note");
  const fileRef = useRef(null);

  const folderVal = folder === "__auto__" ? null : folder;

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "file") {
        const files = fileRef.current?.files;
        if (!files?.length) { toast.error("Choose a file first"); return; }
        for (const f of files) {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("workspace_id", workspaceId);
          if (folderVal) fd.append("folder", folderVal);
          await api.post("/second-brain/documents/upload", fd);
        }
        toast.success(`${files.length} file(s) added — AI is summarizing`);
      } else if (mode === "url") {
        if (!url.trim()) { toast.error("Enter a URL"); return; }
        await api.post("/second-brain/documents/url", { url, workspace_id: workspaceId, folder: folderVal });
        toast.success("URL content added — AI is summarizing");
      } else {
        if (!text.trim()) { toast.error("Paste some text"); return; }
        await api.post("/second-brain/documents/text", {
          title: title || "Untitled", text, workspace_id: workspaceId, folder: folderVal, source_type: sourceType,
        });
        toast.success("Text added — AI is summarizing");
      }
      setUrl(""); setTitle(""); setText("");
      if (fileRef.current) fileRef.current.value = "";
      onAdded();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add knowledge");
    } finally { setBusy(false); }
  };

  const modes = [
    { key: "file", label: "Upload file", icon: FileUp },
    { key: "url", label: "From URL", icon: Globe },
    { key: "text", label: "Paste text", icon: ClipboardType },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Add knowledge</DialogTitle></DialogHeader>
        <div className="flex gap-1 bg-slate-100 rounded-md p-1">
          {modes.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setMode(key)}
              data-testid={`sb-add-mode-${key}`}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs rounded-sm ${
                mode === key ? "bg-white shadow-sm text-[#0a2540] font-medium" : "text-slate-500"
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        <div className="space-y-3 mt-1">
          {mode === "file" && (
            <div>
              <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.md,.csv,.eml,.vtt,.srt" className="block w-full text-xs text-slate-600 file:mr-3 file:px-3 file:py-2 file:border-0 file:rounded-sm file:bg-[#0a2540] file:text-white file:text-xs file:cursor-pointer" data-testid="sb-file-input" />
              <p className="text-[10px] text-slate-400 mt-1.5">PDF, Word, Excel, PowerPoint, TXT, MD, CSV, EML, transcripts (VTT/SRT) · max 25 MB</p>
            </div>
          )}
          {mode === "url" && (
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="sb-url-input" />
          )}
          {mode === "text" && (
            <>
              <div className="flex gap-2">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Board meeting notes — Jan)" className="flex-1" data-testid="sb-text-title-input" />
                <Select value={sourceType} onValueChange={setSourceType}>
                  <SelectTrigger className="w-32 text-xs" data-testid="sb-text-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="transcript">Transcript</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste meeting notes, email threads, transcripts…" rows={7} data-testid="sb-text-input" />
            </>
          )}
          <div className="flex items-center gap-2">
            <span className="gc-label">Folder</span>
            <Select value={folder} onValueChange={setFolder}>
              <SelectTrigger className="h-8 w-48 text-xs" data-testid="sb-folder-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Auto-organize (AI)</SelectItem>
                {folders.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="sb-add-submit-btn">
            {busy && <Loader2 size={13} className="mr-1.5 animate-spin" />} Add to knowledge base
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocDetailDialog({ doc, onClose }) {
  const [reprocessing, setReprocessing] = useState(false);
  if (!doc) return null;
  const reprocess = async () => {
    setReprocessing(true);
    await api.post(`/second-brain/documents/${doc.id}/reprocess`);
    toast.success("Re-analysis started");
    setReprocessing(false);
    onClose();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="sb-doc-detail">
        <DialogHeader><DialogTitle className="pr-6">{doc.title}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-1.5">
            {doc.folder && <Badge variant="outline" className="text-[10px]">{doc.folder}</Badge>}
            {(doc.tags || []).map((t) => <Badge key={t} variant="secondary" className="text-[10px] font-normal">{t}</Badge>)}
          </div>
          {doc.status === "processing" ? (
            <div className="flex items-center gap-2 text-blue-600 text-xs"><Loader2 size={13} className="animate-spin" /> AI is analyzing this document…</div>
          ) : (
            <>
              <div>
                <div className="gc-label mb-1">AI Summary</div>
                <p className="text-[13px] text-slate-700 leading-relaxed">{doc.summary || "No summary available."}</p>
                {doc.processing_error && !doc.summary && (
                  <p className="text-[11px] text-amber-600 mt-1">AI analysis failed — try "Re-analyze". The document is still searchable in chat.</p>
                )}
              </div>
              {(doc.key_decisions || []).length > 0 && (
                <div>
                  <div className="gc-label mb-1">Key decisions & facts</div>
                  <ul className="space-y-1">
                    {doc.key_decisions.map((d, i) => (
                      <li key={i} className="text-[12px] text-slate-700 flex gap-2"><span className="text-[#0a2540] font-bold">·</span>{d}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2.5 flex flex-wrap gap-x-4 gap-y-1">
            <span>Source: {doc.source_type}{doc.original_filename ? ` (${doc.original_filename})` : ""}</span>
            <span>{doc.chunk_count} knowledge chunks</span>
            <span>Added by {doc.uploaded_by_name} on {(doc.created_at || "").slice(0, 10)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={reprocess} disabled={reprocessing} data-testid="sb-doc-reprocess-btn">
            <RefreshCw size={12} className="mr-1.5" /> Re-analyze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
